import { useQuery } from '@tanstack/react-query';
import { useDeferredValue, useState } from 'react';

import { Artwork } from '../dashboard/Artwork';
import { api } from '../../lib/api';
import { copy } from '../../config/copy';
import type { TrackSummary } from '../tracks/TrackList';
import { Verified } from '../../components/ui/Verified';

/**
 * Finding somebody else's track to put in front of a host.
 *
 * ## The half of submission that was missing
 *
 * A room's submit dialog offered your own catalogue and nothing else, which
 * made the earned boost unreachable: it pays out only when the submitter is
 * not the artist, so the one action that earns it had no screen. Everything
 * under it already worked - the API never checked ownership, and the queue has
 * always stored the submitter apart from the artist.
 *
 * ## Search rather than a browse
 *
 * Somebody sending a track to a live room has one in mind. A list to scroll is
 * for discovering; this is for retrieving something already discovered, and the
 * existing endpoint answers it.
 */
export type FoundTrack = TrackSummary;

/** Below this a query matches most of the catalogue and means nothing. */
const MIN_QUERY = 2;

export function FindTracks({ onPick }: { onPick: (track: FoundTrack) => void }) {
  const [term, setTerm] = useState('');

  /*
   * Deferred rather than debounced. The urgency is the request, not the
   * keystroke - React keeps the field responsive and lets the query lag, which
   * is a timer this file does not have to own or clear.
   */
  const query = useDeferredValue(term).trim();
  const long = query.length >= MIN_QUERY;

  const { data, isFetching } = useQuery({
    queryKey: ['search', 'tracks', query],
    queryFn: () =>
      api.get<{ tracks: FoundTrack[] }>(
        '/search?type=tracks&q=' + encodeURIComponent(query),
      ),
    enabled: long,
  });

  /*
   * The same rule the room applies. Search can only return public tracks, but
   * one that is still encoding would be refused at submission, and offering it
   * to be refused explains the rule worse than not offering it.
   */
  const results = (data?.tracks ?? []).filter(
    (track) => track.status === 'ready' && track.visibility === 'public',
  );

  return (
    <div className="flex flex-col gap-3" data-find-tracks>
      <input
        type="search"
        value={term}
        autoFocus
        data-find-input
        placeholder="Search a track or an artist"
        onChange={(event) => setTerm(event.target.value)}
        className="rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-sm text-ink outline-none transition-colors placeholder:text-faint focus:border-line-strong"
      />

      <p className="text-xs leading-relaxed text-faint">
        Sending a track that is not yours counts toward a {copy['noun.boost.earned']} when the
        host plays it through.
      </p>

      {!long ? null : isFetching && results.length === 0 ? (
        <p className="font-mono text-[11px] text-soft">Looking…</p>
      ) : results.length === 0 ? (
        <p className="font-mono text-[11px] text-soft">Nothing matching that.</p>
      ) : (
        <ul className="flex max-h-[18rem] flex-col gap-2 overflow-y-auto" data-find-results>
          {results.map((track) => (
            <li key={track.id}>
              <button
                type="button"
                data-pick-found={track.id}
                onClick={() => onPick(track)}
                className="flex w-full items-center gap-3 rounded-[var(--radius-card)] border border-line p-3 text-left transition-colors hover:border-line-strong hover:bg-raised"
              >
                <Artwork
                  track={{
                    title: track.title,
                    artwork: null,
                    artwork_small: track.artwork_small ?? null,
                  }}
                  className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-ink">{track.title}</span>
                  {/* Whose it is, which is the whole question on this tab. */}
                  <span className="truncate font-mono text-[11px] text-soft">
                    {track.owner?.display_name ?? track.owner?.username ?? 'Unknown artist'}
                    {track.owner?.verified && <Verified />}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
